import React, { useMemo, useState } from 'react';
import { ClosedToDateCard } from './components/ClosedToDateCard';
import { LoanScenarioTool } from './components/LoanScenarioTool';
import { OfficerBar } from './components/OfficerBar';
import { PayEstimator } from './components/PayEstimator';
import { PipelinePanel } from './components/PipelinePanel';
import { loanOfficers, monthContext } from './data/commissionData';
import type { WhatIfInputs } from './types/commission';
import { buildEstimatorColumns, getOfficer, getPlan } from './utils/commission';
const defaultWhatIf: WhatIfInputs = {
  includePipeline: true,
  extraUnits: 2,
  averageLoanAmount: 290_000,
  sellPrice: 101.0
};
interface AppProps {
  /** Which loan officer the estimator opens on */
  initialOfficerId?: string;
  /** Show the active pipeline detail alongside the scenario tool */
  showPipelineDetail?: boolean;
}
export function App({
  initialOfficerId = loanOfficers[0].id,
  showPipelineDetail = true
}: AppProps) {
  const [officerId, setOfficerId] = useState(initialOfficerId);
  const [whatIf, setWhatIf] = useState<WhatIfInputs>(defaultWhatIf);
  const officer = getOfficer(officerId);
  const plan = getPlan(officer.planId);
  const columns = useMemo(() => buildEstimatorColumns(officer, plan, whatIf), [officer, plan, whatIf]);
  const [actuals, pacing] = columns;
  const daysRemaining = monthContext.businessDaysTotal - monthContext.businessDaysElapsed;
  return <div className="min-h-screen w-full bg-slate-50 font-sans text-slate-900" data-id="element-0" data-mp="2zmo6bandcp36">
      <header className="border-b border-slate-200 bg-white" data-id="element-1" data-mp="33nq4do5juv3k">
        <div className="mx-auto max-w-[1400px] px-6 py-5" data-id="element-2" data-mp="38lrs4mdx7ild">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-700" data-id="element-3" data-mp="38kgl8dp6huq1">
            United Mortgage Lending
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900" data-id="element-4" data-mp="nvj179a902mk">Incentive-Based Pay Estimator</h1>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5" data-mp="sudk0b87tr6o">
            Commissions earned, single-loan scenarios, and projected comp with remaining pipeline —
            priced on each banker&rsquo;s own tier schedule.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-5 px-6 py-6" data-id="element-6" data-mp="3ucjc5sple9t7">
        <OfficerBar officer={officer} plan={plan} onOfficerChange={setOfficerId} data-id="element-7" data-mp="d3mlpfjtr3m9" />

        <ClosedToDateCard officer={officer} actuals={actuals} monthLabel={monthContext.label} data-id="element-8" data-mp="851m9icecnyv" />

        <PayEstimator plan={plan} columns={columns} whatIf={whatIf} onWhatIfChange={setWhatIf} onReset={() => setWhatIf(defaultWhatIf)} monthLabel={monthContext.label} daysRemaining={daysRemaining} data-id="element-9" data-mp="3glwcnyc0cfw2" />

        <div className={showPipelineDetail ? 'grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]' : 'grid gap-5'} data-id="element-10" data-mp="3ljg2zwlzcj4d">
          <LoanScenarioTool officer={officer} plan={plan} unitsBefore={whatIf.includePipeline ? pacing.units : actuals.units} data-id="element-11" data-mp="2swbuhhnohmnu" />
          {showPipelineDetail ? <PipelinePanel officer={officer} data-id="element-12" data-mp="1d4zsequzwyqg" /> : null}
        </div>
      </main>
    </div>;
}